/** A API fica no mesmo domínio, em /api (nginx em produção, proxy do Vite em desenvolvimento). */
export const API_URL = ((import.meta.env.VITE_API_URL as string | undefined) || '/api').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); }
}

/** Avisa a tela de que a sessão acabou (vencida ou encerrada) para mostrar o login. */
export const SESSION_LOST = 'quadro:sessao-perdida';

/**
 * Chama uma operação da API do Quadro. A sessão vai no cookie httpOnly (o navegador envia;
 * o código da página não tem acesso ao token). O banco não é acessível pelo navegador.
 */
export async function rpc<T = unknown>(op: string, args?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/rpc`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ op, args }),
    });
  } catch {
    throw new ApiError('Sem conexão com o servidor. Tente de novo em instantes.', 0);
  }
  const body = (await res.json().catch(() => ({}))) as { data?: T; error?: string; code?: string };
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(SESSION_LOST));
    throw new ApiError(body.error ?? 'Não foi possível concluir agora.', res.status, body.code);
  }
  return body.data as T;
}

/** Envia uma operação mesmo com a página fechando (keepalive); sem resposta. */
export function rpcBeacon(op: string, args?: unknown) {
  void fetch(`${API_URL}/rpc`, {
    method: 'POST', credentials: 'same-origin', keepalive: true,
    headers: { 'content-type': 'application/json' }, body: JSON.stringify({ op, args }),
  }).catch(() => undefined);
}

export const loginUrl = `${API_URL}/auth/microsoft/login`;

/** Perfil de quem está logado, ou null sem sessão. */
export async function fetchSession<T>(): Promise<T | null> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/auth/session`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError('Sem conexão com o servidor. Tente de novo em instantes.', 0);
  }
  const body = (await res.json().catch(() => ({}))) as { data?: T | null; error?: string };
  if (!res.ok) throw new ApiError(body.error ?? 'Não foi possível carregar seu perfil.', res.status);
  return body.data ?? null;
}

/** GET simples na API (mesmo tratamento de erro e de sessão do rpc). */
export async function apiGet<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError('Sem conexão com o servidor. Tente de novo em instantes.', 0);
  }
  const body = (await res.json().catch(() => ({}))) as { data?: T; error?: string; code?: string };
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(SESSION_LOST));
    throw new ApiError(body.error ?? 'Não foi possível carregar agora.', res.status, body.code);
  }
  return body.data as T;
}

/** POST na API fora do /rpc (prévia e teste das automações). */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError('Sem conexão com o servidor. Tente de novo em instantes.', 0);
  }
  const data = (await res.json().catch(() => ({}))) as { data?: T; error?: string; code?: string };
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(SESSION_LOST));
    throw new ApiError(data.error ?? 'Não foi possível concluir agora.', res.status, data.code);
  }
  return data.data as T;
}

/**
 * Avisos de mudança da API (SSE): chama onTable com o nome da tabela alterada; onDrop quando a
 * conexão cai e onReopen quando volta (o navegador reconecta sozinho). Devolve a função que fecha.
 */
export function openEvents(h: { onTable: (table: string) => void; onDrop: () => void; onReopen: () => void }): () => void {
  const source = new EventSource(`${API_URL}/events`, { withCredentials: true });
  source.onmessage = e => h.onTable(e.data);
  source.onerror = h.onDrop;
  source.onopen = h.onReopen;
  return () => source.close();
}

export async function signOut() {
  await fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'same-origin' }).catch(() => undefined);
  location.assign('/');
}
